import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"

// Connects to data-controller="settings--deployment-statuses-table"
export default class extends Controller {
    static targets = [
        "table",
        "nameInput",
        "colorInput",
        "editNameInput",
        "editColorInput",
        "editIdInput"
    ]

    connect() {
        this.initializeTable()
        this.loadDeploymentStatuses()
    }

    initializeTable() {
        // No initialization needed — argus--table controller handles this
    }

    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    async loadDeploymentStatuses() {
        const argusTable = this._getArgusTable()
        argusTable?.showLoading()
        try {
            const response = await fetch('/api/deployment_status/list', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch deployment statuses')
            }

            const data = await response.json()

            // The API wraps the response in { status: 'success', data: {...} }
            const responseData = data.data || data
            const statuses = responseData.deployment_statuses || []

            this.populateTable(statuses)
        } catch (error) {
            console.error('Error loading deployment statuses:', error)
            sendFailureNotification('Failed to load deployment statuses')
        } finally {
            argusTable?.hideLoading()
        }
    }

    populateTable(deploymentStatuses) {
        if (this.hasTableTarget) {
            const argusTable = this.application.getControllerForElementAndIdentifier(
                this.tableTarget, 'argus--table'
            )
            if (argusTable) {
                argusTable.loadData(deploymentStatuses)
            }
        }
    }

    openCreateModal(event) {
        event.preventDefault()
        this.showCreateModal()
    }

    showCreateModal() {
        // Clear form
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }
        if (this.hasColorInputTarget) {
            this.colorInputTarget.value = ''
        }

        // Show modal
        const createModal = document.getElementById('addDeploymentStatusModal')
        const modal = bootstrap.Modal.getOrCreateInstance(createModal)
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()

        const form = event.target
        const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]')
        const formData = new FormData(form)

        // Disable button to prevent duplicate submissions
        if (submitBtn) {
            submitBtn.disabled = true
        }

        try {
            const response = await fetch('/api/deployment_status/create', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            const data = await response.json()

            if (!response.ok) {
                // Re-enable button so the user can fix the error and retry
                if (submitBtn) {
                    submitBtn.disabled = false
                }

                const errorMessage = data.message || 'Failed to create deployment status'
                sendFailureNotification(errorMessage)
                return
            }

            // Re-enable button so the modal can be reused for another creation
            if (submitBtn) {
                submitBtn.disabled = false
            }

            // Close modal
            const createModal = document.getElementById('addDeploymentStatusModal')
            const modal = bootstrap.Modal.getInstance(createModal)
            if (modal) {
                modal.hide()
            }

            // Reload table
            await this.loadDeploymentStatuses()

            sendSuccessNotification('Deployment status created successfully')
        } catch (error) {
            // Re-enable button so the user can retry
            if (submitBtn) {
                submitBtn.disabled = false
            }

            console.error('Error creating deployment status:', error)
            sendFailureNotification('Failed to create deployment status')
        }
    }

    openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const statusId = button.dataset.deploymentStatusId

        this.fetchDeploymentStatusDetails(statusId)
    }

    async fetchDeploymentStatusDetails(statusId) {
        try {
            const response = await fetch(`/api/deployment_status/describe?id=${ statusId }`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch deployment status details')
            }

            const result = await response.json()
            const data = result.data || result

            // Populate edit form
            if (this.hasEditNameInputTarget) {
                this.editNameInputTarget.value = data.name || ''
            }
            if (this.hasEditColorInputTarget) {
                this.editColorInputTarget.value = data.status_color || ''
            }
            if (this.hasEditIdInputTarget) {
                this.editIdInputTarget.value = data.id
            }

            // Show modal
            const editModal = document.getElementById('editDeploymentStatusModal')
            const modal = bootstrap.Modal.getOrCreateInstance(editModal)
            modal.show()
        } catch (error) {
            console.error('Error fetching deployment status details:', error)
            sendFailureNotification('Failed to load deployment status details')
        }
    }

    async handleEditSubmit(event) {
        event.preventDefault()

        const form = event.target
        const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]')
        const formData = new FormData(form)

        // Disable button to prevent duplicate submissions
        if (submitBtn) {
            submitBtn.disabled = true
        }

        try {
            const response = await fetch('/api/deployment_status/update', {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            const data = await response.json()

            if (!response.ok) {
                // Re-enable button so the user can fix the error and retry
                if (submitBtn) {
                    submitBtn.disabled = false
                }

                const errorMessage = data.message || 'Failed to update deployment status'
                sendFailureNotification(errorMessage)
                return
            }

            // Re-enable button so the modal can be reused
            if (submitBtn) {
                submitBtn.disabled = false
            }

            // Close modal
            const editModal = document.getElementById('editDeploymentStatusModal')
            const modal = bootstrap.Modal.getInstance(editModal)
            if (modal) {
                modal.hide()
            }

            // Reload table
            await this.loadDeploymentStatuses()

            sendSuccessNotification('Deployment status updated successfully')
        } catch (error) {
            // Re-enable button so the user can retry
            if (submitBtn) {
                submitBtn.disabled = false
            }

            console.error('Error updating deployment status:', error)
            sendFailureNotification('Failed to update deployment status')
        }
    }

    async deleteDeploymentStatus(event) {
        event.preventDefault()

        const button = event.currentTarget
        const statusId = button.dataset.deploymentStatusId

        const row = button.closest('tr')
        const nameCell = row.querySelector('td:first-child')
        const statusName = nameCell ? nameCell.textContent : 'this deployment status'

        if (!confirm(`Are you sure you want to delete ${ statusName }?`)) {
            return
        }

        const formData = new FormData()
        formData.append('id', statusId)

        try {
            const response = await fetch('/api/deployment_status/delete', {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.error || 'Failed to delete deployment status')
            }

            await this.loadDeploymentStatuses()

            sendSuccessNotification('Deployment status deleted successfully')
        } catch (error) {
            console.error('Error deleting deployment status:', error)
            sendFailureNotification(error.message || 'Failed to delete deployment status')
        }
    }
}
